import React, {useState} from "react";
import TextInput from './TextInput.tsx';
import FileInput from './FileInput.tsx';
import Select from './Select.tsx';
import { IClientAccount } from '../../interface/IClientAccount.ts';
import { IOption } from '../../interface/IOption.ts';
import { ApiResponseType } from "../../features/api/types.ts";
import { iso3166 } from "../../static/iso3166.js";
import { useClientAccountApi } from "../../features/auth/hooks/use-client-account-api.ts";
import Button from "./Button.tsx";
import SingleCheckbox from './SingleCheckbox.tsx';
import '../../scss/components/SingleCheckbox.scss';
import { useValidators } from "../../features/functions/validators.tsx";
import { IProfile } from "../../interface/IProfile.ts";
import { CertSimplified, ICertificate } from "../../interface/ICertificate.ts";
import Datepicker from './Datepicker.tsx';
import { useHelper } from "../../features/functions/helper.tsx";

interface ClientAccountProps {
    wrapperClassName?:string,
    singleClassName?:string,
    inputClientAccount:IClientAccount,
    clientAccountTypePrivate:IOption,
    clientAccountTypeCompany:IOption,
    directDebitPayment:IOption,
    transferPayment:IOption,
    outerIsChangedHandler: Function,
    outerCreationHandler: Function,
    outerIsLoading: boolean,
    profile:IProfile,
    certificates:Array<ICertificate>,
    hasCFEP: boolean,
    hasCFP: boolean,
    emptyClient: IClientAccount
}
const ClientAccountForm = ({
    inputClientAccount,
    clientAccountTypePrivate,
    clientAccountTypeCompany,
    directDebitPayment,
    transferPayment,
    outerIsChangedHandler,
    outerCreationHandler,
    outerIsLoading,
    profile,
    certificates,
    hasCFEP,
    hasCFP,
    emptyClient
}:ClientAccountProps) => {
    const [errors, setErrors] = useState<any>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [responseMessage, setResponseMessage] = useState<string>('');
    const [responseMessageClass, setResponseMessageClass] = useState<string>('');
    const [clientAccount, setClientAccount] = useState<IClientAccount>(inputClientAccount);
    const [fileUploadErrors, setFileUploadErrors] = useState<String>('');
    const [showIbanChangedWarning, setShowIbanChangedWarning] = useState<boolean>(false);
    
    const { postClientAccount, getClientAccountDebitAuthorizationTemplate } = useClientAccountApi();
    const { validateEmail, validateIban, validateBic } = useValidators();
    const { isBankingCertificate } = useHelper();
    
    const allowedFileTypes = ['application/pdf'];

    const handleChange = (event, name = '', value = null) => {
        let key = name ? name : event.target.name;
        let val = value ? value : event.target.value;
        let clientAccountClone = structuredClone(clientAccount);
        clientAccountClone[key] = val;
        setClientAccount(clientAccountClone);
        outerIsChangedHandler(true);
    };

    const handleAccountTypeChange = (value:any) => {
        let clientAccountClone = structuredClone(clientAccount);
        if (value === clientAccountTypePrivate.id && (!clientAccountClone.bank_account_owner || clientAccountClone.bank_account_owner?.length <= 0)) {
            clientAccountClone.bank_account_owner = [profile.firstname, profile.lastname].filter(n => n).join(' ');
        }
        clientAccountClone['client_account_type'] = value;
        setClientAccount(clientAccountClone);
        outerIsChangedHandler(true);
    };

    const handleIbanChange = (value) => {
        let clientAccountClone = structuredClone(clientAccount);
        clientAccountClone.bank_account_iban = value;
        if (clientAccountClone.bank_account_debit_authorization) {
            setShowIbanChangedWarning(true);
        }
        clientAccountClone.bank_account_debit_authorization = false;
        setClientAccount(clientAccountClone);
        outerIsChangedHandler(true);
    };

    const handleBooleanChange = (name:string, value:boolean) => {
        let clientAccountClone = structuredClone(clientAccount);
        clientAccountClone[name] = value;
        setClientAccount(clientAccountClone);
        outerIsChangedHandler(true);
    };

    const handleCountryChange = (value:IOption|null) => {
        let clientAccountClone = structuredClone(clientAccount);
        clientAccountClone['client_account_country'] = value;
        setClientAccount(clientAccountClone);
        outerIsChangedHandler(true);
    };

    const handleFileChange = (file:File) => {
        
        let fileName = file.name;
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            let result = reader.result;
            if (result) {
                let clientAccountClone = structuredClone(clientAccount);
                clientAccountClone.payload = String(result).split(',')[1];
                clientAccountClone.filename = fileName;
                setClientAccount(clientAccountClone);
                outerIsChangedHandler(true);
            }
        }, false);
        reader.readAsDataURL(file);

    }

    const handleSubmit = (event) => {
        event.preventDefault();

        outerIsChangedHandler(false);
        setIsLoading(true);
        setErrors({});
        
        let clientAccountClone = structuredClone(clientAccount);

        //Check for valid certs if the user has any
        if (certificates.length > 0) {
            if (clientAccountClone.assigned_certificates && clientAccountClone.assigned_certificates.length > 0) {
                //Push certs into clone to be passed to api
                let certificateIds:Array<number> = [];
                clientAccountClone.assigned_certificates.forEach((cert:CertSimplified) => {
                    certificateIds.push(cert.id);
                })
                //@ts-ignore
                clientAccountClone.assigned_certificates = certificateIds;            
            } else {
                //No cert selected - show error
                setIsLoading(false);
                setErrors({
                    client_account_certificates: 'Bitte wählen Sie mindestens ein Zertifikat aus, dem das Rechnungskonto zugeordnet werden soll.'
                });
                let certificateRow = document.getElementById('certificate-row');
                if (certificateRow) {
                    setTimeout(() => {
                        var elementPosition = certificateRow.getBoundingClientRect().top;
                        var offsetPosition = elementPosition + window.pageYOffset;
                        window.scrollTo({
                            top: offsetPosition,
                            behavior: "smooth"
                        });
                    }, 0);
                }
                return;
            }
        }

        if (clientAccountClone.client_account_country) {
            clientAccountClone.client_account_country = clientAccountClone.client_account_country.value;
        }

        if (clientAccountClone.client_account_mandate_valid_from && typeof clientAccountClone.client_account_mandate_valid_from === 'object') {
            let dateValue = Math.floor(clientAccountClone.client_account_mandate_valid_from!.getTime() / 1000);
            clientAccountClone.client_account_mandate_valid_from = dateValue;
        }

        //Create new Account
        postClientAccount(clientAccountClone).then(res => {
            setIsLoading(false);
            if (res.type === ApiResponseType.OK) {
                handleResponseSuccess(res);
                outerCreationHandler(clientAccountClone, res.payload.account_id, res.payload.bank_account_sepa_mandate_reference, res.payload.name, clientAccount.assigned_certificates);
                resetToEmpty();
            } else {
                handleResponseError(res.payload.errors!);
            }
        })
        
    }

    const handleResponseSuccess = (res) => {
        if (res.payload.success)  {
            setResponseMessage('Erfolgreich gespeichert');
            setResponseMessageClass('success show');
            setTimeout(() => {
                setResponseMessage('');
            }, 5000);
        }
    }

    const handleResponseError = (errors) => {
        if (!errors.general_error) {
            setResponseMessage('Fehler. Bitte korrigieren Sie den Fehler und versuchen Sie es erneut.');
            setResponseMessageClass('error show');
        }
        setErrors(errors);
    }

    const resetToEmpty = () => {
        setClientAccount(emptyClient);
        outerIsChangedHandler(false, true);
        let formTop = document.getElementById('scroll-to-headline');
        if (formTop) {
            setTimeout(() => {
                var headerOffset = 0;
                var elementPosition = formTop.getBoundingClientRect().top;
                var offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth"
                });
            }, 0);
        }
    }

    const downloadMandatPDF = () => {
        getClientAccountDebitAuthorizationTemplate().then(res => {
            if (res.type === ApiResponseType.OK) {
                let base64String = 'data:' + res.payload.content_type + ';base64,' + res.payload.payload;
                var a = document.createElement("a");
                a.href = base64String
                a.download = "SEPA-Mandat.pdf";
                a.click();
            } else {
                handleResponseError(res.payload.errors!);
            }
        })
    }

    return (
        <form onSubmit={handleSubmit} className={`client-account-form single-account-wrapper${isLoading || outerIsLoading ? ' loading' : ''}`} id="client-account-form">
            <>
                <div className="button-pills">
                    <h6>Art des Rechnungskontos</h6>
                    <div className="button-wrapper">
                        <Button
                            buttonType='primary'
                            type='button'
                            className={clientAccount.client_account_type === clientAccountTypePrivate.id ? '' : 'grey'}
                            onClick={(e) => {
                                e.preventDefault();
                                handleAccountTypeChange(clientAccountTypePrivate.id);
                            }}
                        >{ clientAccountTypePrivate.label }</Button>
                        <Button
                            buttonType='primary'
                            type='button'
                            className={clientAccount.client_account_type === clientAccountTypeCompany.id ? '' : 'grey'}
                            onClick={(e) => {
                                e.preventDefault();
                                handleAccountTypeChange(clientAccountTypeCompany.id);
                            }}
                        >{ clientAccountTypeCompany.label }</Button>
                    </div>
                    {errors['client_account_type'] && <span className='form-error' role='alert'>{ errors['client_account_type'] }<br></br></span>}
                </div>
                <div className={`form-content${!clientAccount.client_account_type ? ' disabled' : ''}`}>
                    {certificates.length > 0 && (<>
                    <h6>Auswahl der Zertifikate für das neue Rechnungskonto</h6>
                    <div className="controls-row row" id="certificate-row">
                        {certificates.map((certificate: ICertificate, key: number) => {
                            if (!isBankingCertificate(certificate.type)) {
                                return '';
                            }
                            let isSelected = clientAccount.assigned_certificates && clientAccount.assigned_certificates.findIndex(i => { return i.id === certificate.id; });
                            let isInActive = (certificate.type === 'EFA' && hasCFP) || (certificate.type === 'CGA' && hasCFEP);
                            return (
                                <div className={`certificate-select-item${isSelected !== -1 ? ' selected' : ''}${isInActive ? ' inactive' : ''}`} key={'cert_item_' + key} onClick={() => {
                                    let clientAccountClone = structuredClone(clientAccount);
                                    if (isInActive) return;
                                    if (isSelected === -1) {
                                        clientAccountClone.assigned_certificates.push({
                                            id: certificate.id,
                                            type: certificate.type,
                                        });
                                    } else {
                                        clientAccountClone.assigned_certificates.splice(isSelected,1);                                
                                    }
                                    setClientAccount(clientAccountClone);
                                } }>
                                    <p>{certificate.type}</p>
                                    {certificate.type === 'EFA' && hasCFP && (
                                        <span className="tooltip">Bei gleichzeitiger CFP®-Zertifizierung fallen keine laufenden Lizenzgebühren an. Die Abrechnung mit 100% Rabatt erfolgt über das Rechnungskonto des CFP®-Zertifikats</span>
                                    )}
                                    {certificate.type === 'CGA' && hasCFEP && (
                                        <span className="tooltip">Bei gleichzeitiger CFEP®-Zertifizierung fallen keine laufenden Lizenzgebühren an. Die Abrechnung mit 100% Rabatt erfolgt über das Rechnungskonto des CFEP®-Zertifikats.</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    { errors['client_account_certificates'] && (
                        <span className='form-error' role='alert'>{ errors['client_account_certificates'] }<br></br></span>
                    ) }
                    </>)}
                    <div className="grid">
                        <h6>Rechnungsadresse</h6>
                        <TextInput
                            onChange={(e) => handleChange(e, 'client_account_billing_name')}
                            value={clientAccount.client_account_billing_name}
                            label={`Rechnungsempfänger${clientAccount.client_account_type === clientAccountTypeCompany.id ? ' (Unternehmen)' : ''}`}
                            name={'client_account_billing_name-' + clientAccount.id}
                            error={errors['client_account_billing_name']}
                            required={true}
                        />
                        { clientAccount.client_account_type === clientAccountTypeCompany.id ? (
                            <>
                                <TextInput
                                    onChange={(e) => handleChange(e, 'client_account_department')}
                                    value={clientAccount.client_account_department}
                                    label='Rechnungsempfänger (Abteilung)'
                                    name={'client_account_department-' + clientAccount.id}
                                    error={errors['client_account_department']}
                                />
                                <TextInput
                                    onChange={(e) => handleChange(e, 'client_account_contact_person')}
                                    value={clientAccount.client_account_contact_person}
                                    label='Rechnungsempfänger (Ansprechpartner)'
                                    name={'client_account_contact_person-' + clientAccount.id}
                                    error={errors['client_account_contact_person']}
                                />
                                <TextInput
                                    onChange={(e) => handleChange(e, 'client_account_cost_position')}
                                    value={clientAccount.client_account_cost_position}
                                    label='Kostenstelle'
                                    name={'client_account_cost_position-' + clientAccount.id}
                                    error={errors['client_account_cost_position']}
                                />
                                <TextInput
                                    onChange={(e) => handleChange(e, 'client_account_budget_number')}
                                    value={clientAccount.client_account_budget_number}
                                    label='Budget-Nr.'
                                    name={'client_account_budget_number-' + clientAccount.id}
                                    error={errors['client_account_budget_number']}
                                />
                                <TextInput
                                    onChange={(e) => handleChange(e, 'client_account_project_number')}
                                    value={clientAccount.client_account_project_number}
                                    label='Projektnummer'
                                    name={'client_account_project_number-' + clientAccount.id}
                                    error={errors['client_account_project_number']}
                                />
                            </>
                        ) : '' }
                        <TextInput
                            onChange={(e) => handleChange(e, 'client_account_address')}
                            value={clientAccount.client_account_address}
                            label='Straße, Hausnummer'
                            name={'client_account_address-' + clientAccount.id}
                            error={errors['client_account_address']}
                            required={true}
                        />
                        { clientAccount.client_account_type === clientAccountTypeCompany.id ? (
                            <TextInput
                                onChange={(e) => handleChange(e, 'client_account_address_extra')}
                                value={clientAccount.client_account_address_extra}
                                label='Rechnungsempfänger (Adresszusatz)'
                                name={'client_account_address_extra-' + clientAccount.id}
                                error={errors['client_account_address_extra']}
                                required={false}
                            />
                        ) : '' }
                        <TextInput
                            onChange={(e) => handleChange(e, 'client_account_zip')}
                            value={clientAccount.client_account_zip}
                            label='PLZ'
                            name={'client_account_zip-' + clientAccount.id}
                            error={errors['client_account_zip']}
                            required={true}
                        />
                        <TextInput
                            onChange={(e) => handleChange(e, 'client_account_city')}
                            value={clientAccount.client_account_city}
                            label='Ort'
                            name={'client_account_city-' + clientAccount.id}
                            error={errors['client_account_city']}
                            required={true}
                        />
                        <Select
                            isSearchable={true}
                            isClearable={true}
                            label='Land'
                            placeholder='Land auswählen'
                            name={'client_account_country-' + clientAccount.id}
                            value={clientAccount.client_account_country}
                            options={iso3166}
                            onChange={(e, action) => {
                                if (e && e.value) {
                                    handleCountryChange(e)
                                }
                                if (!e && action.action === 'clear') {
                                    handleCountryChange(null)
                                }
                            }}
                            error={errors['client_account_country']}
                            isRequired={true}
                        />
                        <TextInput
                            onChange={(e) => {
                                handleChange(e, 'client_account_email')
                                let errorClone = {...errors}
                                if (!validateEmail(e.target.value)) {
                                    errorClone['client_account_email'] = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
                                    setErrors(errorClone)
                                } else {
                                    delete errorClone['client_account_email'];
                                    setErrors(errorClone);
                                }
                            }}
                            value={clientAccount.client_account_email}
                            label='E-Mail'
                            name={'client_account_email-' + clientAccount.id}
                            error={errors['client_account_email']}
                            required={true}
                        />
                        { clientAccount.client_account_type === clientAccountTypeCompany.id ? (
                            <TextInput
                                onChange={(e) => handleChange(e, 'client_account_ust_id')}
                                value={clientAccount.client_account_ust_id}
                                label='UST-ID / VAT'
                                name={'client_account_ust_id-' + clientAccount.id}
                                error={errors['client_account_ust_id']}
                            />
                        ) : '' }
                    </div>
                    <div className="grid">
                        <div className="fullwidth-column">
                            <h6>Zahlungsmethode für das neues Rechnungskonto</h6>
                            <div className="button-pills">
                                <div className="button-wrapper">
                                    <Button
                                        buttonType='primary'
                                        type='button'
                                        className={clientAccount.client_account_payment_type === directDebitPayment.id ? '' : 'grey'}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleChange(e, 'client_account_payment_type', directDebitPayment.id!);
                                        }}
                                    >{ directDebitPayment.label }</Button>
                                    <Button
                                        buttonType='primary'
                                        type='button'
                                        className={clientAccount.client_account_payment_type === transferPayment.id ? '' : 'grey'}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleChange(e, 'client_account_payment_type', transferPayment.id!);
                                        }}
                                    >{ transferPayment.label }</Button>
                                </div>
                            </div>
                        </div>
                        {/* Nur Privat Lastschrift Zahlungsarten */}
                        { clientAccount.client_account_payment_type === directDebitPayment.id && clientAccount.client_account_type === clientAccountTypePrivate.id ? (
                            <>
                                <TextInput
                                    onChange={(e) => handleChange(e, 'bank_account_owner')}
                                    value={clientAccount.bank_account_owner}
                                    label='Kontoinhaber'
                                    name={'bank_account_owner-' + clientAccount.id}
                                    error={errors['bank_account_owner']}
                                    required={true}
                                />
                                <TextInput
                                    onChange={(e) => {
                                        handleIbanChange(e.target.value)
                                        let errorClone = {...errors}
                                        if (!validateIban(e.target.value)) {
                                            errorClone['bank_account_iban'] = 'Bitte geben Sie eine gültige IBAN ein';
                                            setErrors(errorClone)
                                        } else {
                                            delete errorClone['bank_account_iban'];
                                            setErrors(errorClone);
                                        }
                                    }}
                                    value={clientAccount.bank_account_iban}
                                    label='IBAN'
                                    name={'bank_account_iban-' + clientAccount.id}
                                    error={errors['bank_account_iban']}
                                    required={true}
                                />
                                { showIbanChangedWarning && (
                                    <p className="message warning">Wenn Sie Ihre IBAN ändern müssen Sie die Einzugsermächtigung erneut erlauben.</p>
                                ) }
                                <TextInput
                                    onChange={(e) => {
                                        handleChange(e, 'bank_account_bic')
                                        let errorClone = {...errors}
                                        if (!validateBic(e.target.value, clientAccount.bank_account_iban!)) {
                                            errorClone['bank_account_bic'] = 'Bitte geben Sie eine gültige BIC ein';
                                            setErrors(errorClone)
                                        } else {
                                            delete errorClone['bank_account_bic'];
                                            setErrors(errorClone);
                                        }
                                    }}
                                    value={clientAccount.bank_account_bic}
                                    label={`BIC${ clientAccount.client_account_payment_type === transferPayment.id! ? ' / SWIFT-Code' : '' }`}
                                    name={'bank_account_bic-' + clientAccount.id}
                                    error={errors['bank_account_bic']}
                                    required={true}
                                />
                                <TextInput
                                    onChange={(e) => handleChange(e, 'bank_account_name')}
                                    value={clientAccount.bank_account_name}
                                    label='Kreditinstitut'
                                    name={'bank_account_name-' + clientAccount.id}
                                    error={errors['bank_account_name']}
                                    required={true}
                                />
                            </>
                        ) : '' }
                        {/* SEPA-Lastschrift + private Kontoart */}
                        { clientAccount.client_account_payment_type === directDebitPayment.id! && clientAccount.client_account_type === clientAccountTypePrivate.id ? (
                            <>
                                <Datepicker
                                    selected={clientAccount.client_account_mandate_valid_from}
                                    onChange={(date) => {
                                        handleChange(null, 'client_account_mandate_valid_from', date);
                                    }}
                                    locale='de'
                                    dateFormat='dd.MM.yyyy'
                                    showYearDropdown={true}
                                    showMonthDropdown={true}
                                    label='SEPA-Mandat gültig ab'
                                    isRequired={true}
                                    name='client_account_mandate_valid_from'
                                    error={errors['client_account_mandate_valid_from']}
                                />
                                <SingleCheckbox
                                    onChange={(e) => {
                                        handleBooleanChange('bank_account_debit_authorization', e.target.checked);
                                    }}
                                    checked={clientAccount.bank_account_debit_authorization}
                                    name={'bank_account_debit_authorization-' + clientAccount.id}
                                    label='Ich ermächtige den FPSB Deutschland e.V. (Gläubiger-Identifikationsnummer: DE46ZZZ00000625761) den jährlichen FPSB-Mitgliedsbeitrag sowie sonstige Gebühren bei Fälligkeit von meinem nachfolgend genannten Konto mittels Lastschrift einzuziehen. Zugleich weise ich das Kreditinstitut an, die vom FPSB Deutschland gezogenen Lastschriften einzulösen. Das Lastschriftmandat gilt unbefristet bis zu seinem Widerruf. Hinweis für SEPA-Basis-Lastschrift: Ich kann innerhalb von 8 Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit meinem Kreditinstitut vereinbarten Bedingungen.'
                                    labelPosition='after'
                                    className={`checkbox-sm inline ${showIbanChangedWarning ? 'warning' : ''}`}
                                    error={errors['bank_account_debit_authorization']}
                                />
                            </>
                        ) : ''}
                        {/* SEPA-Lastschrift + geschäftliche Kontoart */}
                        { clientAccount.client_account_payment_type === directDebitPayment.id! && clientAccount.client_account_type === clientAccountTypeCompany.id ? (
                            <>
                                <div className="fullwidth-column">
                                    <h6>SEPA-Lastschriftmandat</h6>
                                    <p>Bei geschäftlichen Rechnungskonten ist die Zustimmung des Kontoinhabers zum SEPA-Lastschriftmandat notwendig. <br/>Bitte laden Sie die PDF-beschreibbare Mandatsvorlage zur Vervollständigung herunter, lassen diese vom Kontoinhaber unterschreiben und laden sie anschließend wieder hoch.</p>
                                    <Button
                                        type='button'
                                        buttonType="primary"
                                        icon="download"
                                        onClick={downloadMandatPDF}
                                    >Jetzt Mandatsvorlage herunterladen</Button>
                                    <FileInput
                                        label="SEPA-Lastschriftmandat hochladen"
                                        name={'payload-' + clientAccount.id}
                                        onChange={(event) => {
                                            const [file] = event.target.files;
                                            if (allowedFileTypes.includes(file.type)) {
                                                if (file) {
                                                    handleFileChange(file);
                                                }
                                            } else {
                                                setFileUploadErrors('Dateiart nicht erlaubt');
                                            }
                                        }}
                                        accept={allowedFileTypes.join(', ')}
                                    error={errors['payload']}
                                    />
                                    { clientAccount.filename && (
                                        <div className="mt-3">                                        
                                            <p className="mb-1">Die Datei wird durch Speichern des Formulars hochgeladen. Es erfolgt eine Prüfung und Erfassung durch den FPSB Deutschland. Sie erhalten eine E-Mail, wenn der Prozess abgeschlossen ist.</p>
                                            <span className="message">Gewählte Datei: {clientAccount.filename}</span>
                                        </div>
                                    ) }
                                    { fileUploadErrors && (
                                        <span className="message error">{fileUploadErrors}</span>
                                    )}
                                </div>
                            </>
                        ) : ''}
                    </div>
                </div>
                { responseMessage && (
                    <div className={`message${responseMessageClass ? ' ' + responseMessageClass : ''}`} role="alert" onClick={() => {setResponseMessage('');}}>
                        <span>
                            {responseMessage}
                        </span>
                    </div>
                ) }
                <div className={`form-controls row${!clientAccount.client_account_type ? ' disabled' : ''}`}>
                    <div className="d-flex align-items-center row">
                        <Button
                            buttonType='primary'
                            type='submit'
                            icon='check'
                        >Neues Rechnungskonto hinzufügen</Button>
                        <Button buttonType='filled-danger' onClick={resetToEmpty}>Änderungen verwerfen</Button>
                    </div>
                </div>
            </>
        </form>
    )
}

export default ClientAccountForm;
